import axios, { AxiosRequestConfig } from 'axios';
import React from 'react';

/**
 * Checks if a given URL is part of a specified list of domains.
 *
 * Domains can be marked as ignored by prefixing them with an exclamation mark (!).
 * If a URL matches any ignored domain, it will be considered untrusted.
 *
 * @param {string} [url=''] - The URL to be checked.
 * @param {string[]} [domainList=[]] - An array of domains, which may include ignored domains.
 * @returns {boolean} - Returns `true` if the URL is in the domain list, otherwise `false`.
 *
 * @example
 * const domainList = ['example.com', '!example.org'];
 * console.log(isUrlInDomainList('https://example.com/page', domainList)); // true
 * console.log(isUrlInDomainList('https://example.org/page', domainList)); // false
 * console.log(isUrlInDomainList('https://nottrusted.com', domainList)); // false
 */
export const isUrlInDomainList = (url: string = '', domainList: string[] = []): boolean => {
  if (!url || !domainList.length) return false;

  const ignoredURLs = domainList
    .filter((domain) => domain.startsWith('!'))
    .map((domain) => domain.replace(/^!/, ''));
  const domains = domainList.filter((domain) => !domain.startsWith('!'));

  if (ignoredURLs.includes(url)) return false;

  const domainFound = domains.find((domain) => url.startsWith(domain));
  return Boolean(domainFound);
};

export const useRequiredInterceptor = ({
  isReady,
  isError,
  onRequired,
  onError,
  trustedDomains,
  log = console.log,
}: {
  isReady: boolean;
  isError?: boolean;
  onRequired: () => void;
  onError?: (config: AxiosRequestConfig) => AxiosRequestConfig;
  trustedDomains: string[];
  log?: (...args: any[]) => void;
}) => {
  const isRequired = React.useRef(false);
  const isReadyRef = React.useRef(false);
  const isErrorRef = React.useRef(false);
  const waitingForReadiness = React.useRef<(() => void)[]>([]);
  const waitingForError = React.useRef<(() => void)[]>([]);

  // On Ready
  React.useEffect(() => {
    // Resolve pending requests if isReady is true
    if (isReady) {
      isReadyRef.current = true;

      // Send resolution to the end of the eventloop
      new Promise((resolve) => setTimeout(resolve)).then(() => {
        waitingForReadiness.current.forEach((resolve) => resolve());
        waitingForReadiness.current = [];
      });
    } else {
      isReadyRef.current = false;
      isRequired.current = false;
    }
  }, [isReady]);

  // On Error
  React.useEffect(() => {
    // Resolve pending requests if isError is true
    if (isError) {
      isErrorRef.current = true;

      // Send resolution to the end of the eventloop
      new Promise((resolve) => setTimeout(resolve)).then(() => {
        waitingForError.current.forEach((reject) => reject());
        waitingForError.current = [];
      });
    } else {
      isErrorRef.current = false;
    }
  }, [isError]);

  /**
   * Interceptor
   *
   * Validates trusted domains and then call initialization waiting for readiness
   */
  React.useEffect(() => {
    const ejectId = axios.interceptors.request.use(async (config) => {
      if (!isUrlInDomainList(config.url, trustedDomains)) return config;

      if (isReadyRef.current) {
        return config;
      }

      try {
        if (isErrorRef.current) {
          await Promise.reject();
        }

        log('Request intercepted:', config.url);

        if (!isRequired.current) {
          isRequired.current = true;
          onRequired();
        }

        await new Promise<void>((resolve, reject) => {
          waitingForReadiness.current.push(resolve);
          waitingForError.current.push(reject);
        });

        log('Resolved request intercepted', config.url);

        return config;
      } catch (e) {
        log('Error request intercepted', config.url);
        return onError ? onError(config) : config;
      }
    });

    return () => {
      axios.interceptors.request.eject(ejectId);
    };
  }, [log, onError, onRequired, trustedDomains]);
};
